import { createPathComponent } from '@react-leaflet/core';
import { useState, useMemo, useCallback, createContext, useContext } from 'react';
import L from 'leaflet';

// Расширенная палитра цветов для полигонов (24 цвета, без дублей)
export const colors = [
  // Tailwind-подобные (хорошо различимы на карте)
  [59, 130, 246],   // blue-500
  [34, 197, 94],    // green-500
  [245, 158, 11],   // amber-500
  [168, 85, 247],   // purple-500
  [236, 72, 153],   // pink-500
  [14, 165, 233],   // sky-500
  [239, 68, 68],    // red-500
  [20, 184, 166],   // teal-500
  [99, 102, 241],   // indigo-500
  [132, 204, 22],   // lime-500
  [249, 115, 22],   // orange-500
  [6, 182, 212],    // cyan-500
  // Ещё 12 (чтобы редко повторялись)
  [124, 58, 237],   // violet-600
  [234, 88, 12],    // orange-600
  [22, 163, 74],    // green-600
  [37, 99, 235],    // blue-600
  [190, 24, 93],    // pink-700
  [180, 83, 9],     // amber-700
  [17, 94, 89],     // teal-700
  [30, 64, 175],    // indigo-800
  [67, 56, 202],    // indigo-700
  [21, 128, 61],    // emerald-700
  [159, 18, 57],    // rose-800
  [100, 116, 139],  // slate-500 (на случай “приглушённого”)
];

// Кэш для быстрого доступа к цвету по индексу
const colorsLength = colors.length;

// Предвычисленные rgba строки для всех цветов (при загрузке модуля)
// Структура: colorStrings[colorIndex] = { stroke06, stroke1, grad018, grad06 }
const colorStrings = colors.map(rgb => {
  const r = rgb[0], g = rgb[1], b = rgb[2];
  return {
    stroke06: `rgba(${r},${g},${b},0.6)`,  // обводка обычная
    stroke1: `rgba(${r},${g},${b},1)`,     // обводка при hover
    grad018: `rgba(${r},${g},${b},0.1)`,  // градиент 0.5 stop
    grad06: `rgba(${r},${g},${b},0.4)`,    // градиент 1.0 stop
  };
});

// Быстрый доступ к предвычисленным строкам
function getStrokeColor(colorIndex, isHovered) {
  const c = colorStrings[colorIndex % colorsLength];
  return isHovered ? c.stroke1 : c.stroke06;
}

function getGradientColors(colorIndex) {
  const c = colorStrings[colorIndex % colorsLength];
  return [c.grad018, c.grad06];
}

// Контекст для передачи общего renderer всем полигонам
const RendererContext = createContext(null);

const TOOLTIP_CLASS = 'hp-tooltip';

// Инжектируем стили один раз при загрузке модуля
if (typeof document !== 'undefined' && !document.getElementById('hp-tooltip-styles')) {
  const style = document.createElement('style');
  style.id = 'hp-tooltip-styles';
  style.textContent = `
/* Оптимизированные стили для быстрой отрисовки */
.leaflet-tooltip.${TOOLTIP_CLASS} {
  border: 0;
  background: rgba(15, 23, 42, 0.95);
  color: #fff;
  padding: 6px 10px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  font: 500 13px/1.3 system-ui, -apple-system, sans-serif;
  white-space: nowrap;
  /* Убрали backdrop-filter — очень медленный! */
  will-change: transform;
  pointer-events: none;
}
.leaflet-tooltip.${TOOLTIP_CLASS}::before {
  border-top-color: rgba(15, 23, 42, 0.95);
}
`;
  document.head.appendChild(style);
}

// Используем простой текст вместо DOM-дерева для максимальной производительности
function buildTooltipContent(title) {
  // Для производительности используем просто текст
  // colorIndex не используется, убрали лишний параметр
  return title ?? '';
}

/**
 * Провайдер, который создаёт один Canvas renderer для группы полигонов
 * Оборачивай им все HighlightablePolygon, чтобы они использовали один canvas
 */
export const PolygonRendererProvider = ({ children, pane = 'resPane' }) => {
  // Важно: renderer — объект, который Leaflet сам добавит на карту при первом
  // использовании (через options.renderer у Path). Нам не нужно вручную addLayer/removeLayer.
  const renderer = useMemo(() => L.canvas({ padding: 0.5, pane }), [pane]);

  return (
    <RendererContext.Provider value={renderer}>
      {children}
    </RendererContext.Provider>
  );
};

// Хук для получения renderer из контекста
const useSharedRenderer = () => useContext(RendererContext);

/**
 * Кастомный класс Polygon с градиентной заливкой
 * Градиент идет от прозрачного центра к цветным краям
 */
export class GradientPolygon extends L.Polygon {
  constructor(latlngs, options) {
    super(latlngs, options);
    this._colorIndex = options.colorIndex || 0;
    this._gradientCacheKey = null;
    this._gradientCache = null;
  }

  initialize(latlngs, options) {
    L.Polygon.prototype.initialize.call(this, latlngs, options);
    this._colorIndex = options.colorIndex || 0;
    this._gradientCacheKey = null;
    this._gradientCache = null;
    return this;
  }

  _updatePath() {
    const renderer = this._renderer;
    if (!renderer || !renderer._ctx) {
      return L.Polygon.prototype._updatePath.call(this);
    }

    const ctx = renderer._ctx;

    // Вызываем стандартную отрисовку полигона (для обводки)
    L.Polygon.prototype._updatePath.call(this);

    // Ключевая оптимизация: Leaflet уже посчитал пиксельные точки в this._parts.
    // Не делаем map.latLngToLayerPoint для каждой вершины ещё раз.
    const parts = this._parts;
    if (!parts || parts.length === 0) return;

    // Оптимизация: используем один проход для вычисления границ и отрисовки
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let hasPoints = false;

    // Вычисляем границы и готовимся к отрисовке
    for (let p = 0; p < parts.length; p++) {
      const ring = parts[p];
      if (!ring || ring.length === 0) continue;
      hasPoints = true;
      
      // Для первого кольца сразу обновляем границы
      for (let i = 0; i < ring.length; i++) {
        const pt = ring[i];
        const x = pt.x;
        const y = pt.y;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }

    if (!hasPoints || !isFinite(minX) || !isFinite(minY)) return;

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const maxRadius = Math.max(maxX - minX, maxY - minY) * 0.5;

    // Выбираем цвет (используем предвычисленные строки)
    const colorIndex = this._colorIndex % colorsLength;
    const gradColors = getGradientColors(colorIndex);

    // Создаем или переиспользуем радиальный градиент
    // Ключ: округляем до целых для лучшего попадания в кэш
    const cx = centerX | 0, cy = centerY | 0, mr = maxRadius | 0;
    const gradientKey = (cx << 20) | (cy << 8) | (mr ^ colorIndex);
    
    let gradient = this._gradientCacheKey === gradientKey ? this._gradientCache : null;
    if (!gradient) {
      gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, maxRadius
      );

      // Градиент: прозрачно в центре, цветной по краям (2 colorStop)
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(0.5, gradColors[0]);
      gradient.addColorStop(1, gradColors[1]);

      this._gradientCacheKey = gradientKey;
      this._gradientCache = gradient;
    }

    // Рисуем полигон с градиентом
    ctx.fillStyle = gradient;
    ctx.beginPath();
    
    for (let p = 0; p < parts.length; p++) {
      const ring = parts[p];
      if (!ring || !ring.length) continue;
      
      const firstPt = ring[0];
      ctx.moveTo(firstPt.x, firstPt.y);
      
      for (let i = 1, len = ring.length; i < len; i++) {
        ctx.lineTo(ring[i].x, ring[i].y);
      }
      ctx.closePath();
    }
    
    ctx.fill();
  }
}


// Функция создания экземпляра полигона
function createGradientPolygon(props, context) {
  const colorIndex = props.colorIndex || 0;
  
  const instance = new GradientPolygon(props.positions, {
    colorIndex,
    fillColor: 'transparent',
    fillOpacity: 0,
    color: getStrokeColor(colorIndex, props.isHovered),
    weight: props.isHovered ? 3 : 1,
    // Используем переданный renderer или создаём новый как fallback
    renderer: props.renderer,
    pane: props.pane || 'resPane',
    interactive: true,
    bubblingMouseEvents: false,
  });

  // Привязываем tooltip через Leaflet API
  if (props.tooltipContent) {
    instance.bindTooltip(buildTooltipContent(props.tooltipContent), {
      sticky: true,
      opacity: 1,
      className: TOOLTIP_CLASS,
      direction: 'top',
      offset: [0, -5],
    });
  }

  // Привязываем обработчики событий вручную (оптимизация: без Object.entries)
  if (props.eventHandlers) {
    const handlers = props.eventHandlers;
    if (handlers.mouseover) instance.on('mouseover', handlers.mouseover);
    if (handlers.mouseout) instance.on('mouseout', handlers.mouseout);
    if (handlers.click) instance.on('click', handlers.click);
  }

  return { instance, context };
}

// Функция обновления полигона
function updateGradientPolygon(instance, props, prevProps) {
  // Обновляем позиции
  if (props.positions !== prevProps.positions) {
    instance.setLatLngs(props.positions);
  }

  // Обновляем цвет при изменении индекса (нужен redraw для градиента)
  if (props.colorIndex !== prevProps.colorIndex) {
    instance._colorIndex = props.colorIndex || 0;
    instance.setStyle({
      color: getStrokeColor(props.colorIndex || 0, props.isHovered),
      weight: props.isHovered ? 3 : 1,
    });
    if (instance._map) instance.redraw();
  }
  // Hover — только обновляем обводку, градиент не меняется
  else if (props.isHovered !== prevProps.isHovered) {
    instance.setStyle({
      color: getStrokeColor(props.colorIndex || 0, props.isHovered),
      weight: props.isHovered ? 3 : 1,
    });
  }

  // Обновляем tooltip при изменении
  if (props.tooltipContent !== prevProps.tooltipContent) {
    const tooltip = instance.getTooltip();
    if (tooltip) {
      instance.setTooltipContent(buildTooltipContent(props.tooltipContent));
    } else if (props.tooltipContent) {
      instance.bindTooltip(buildTooltipContent(props.tooltipContent), {
        sticky: false,
        opacity: 1,
        className: TOOLTIP_CLASS,
        direction: 'top',
        offset: [0, -5],
      });
    }
  }

  // Обновляем обработчики событий при изменении
  // Оптимизация: проверяем только если ссылка изменилась (useMemo в HighlightablePolygon гарантирует стабильность)
  if (props.eventHandlers !== prevProps.eventHandlers) {
    // Удаляем старые обработчики
    if (prevProps.eventHandlers) {
      const prevHandlers = prevProps.eventHandlers;
      // Избегаем Object.entries если возможно
      if (prevHandlers.mouseover) instance.off('mouseover', prevHandlers.mouseover);
      if (prevHandlers.mouseout) instance.off('mouseout', prevHandlers.mouseout);
      if (prevHandlers.click) instance.off('click', prevHandlers.click);
    }
    // Добавляем новые
    if (props.eventHandlers) {
      const handlers = props.eventHandlers;
      if (handlers.mouseover) instance.on('mouseover', handlers.mouseover);
      if (handlers.mouseout) instance.on('mouseout', handlers.mouseout);
      if (handlers.click) instance.on('click', handlers.click);
    }
  }
}

// Создаем React компонент
const GradientPolygonComponent = createPathComponent(createGradientPolygon, updateGradientPolygon);

// Обертка с hover обработчиками
export const HighlightablePolygon = ({ positions, name, colorIndex: propColorIndex, onClick, pane = 'resPane' }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Получаем общий renderer из контекста
  const sharedRenderer = useSharedRenderer();

  // Мемоизируем обработчики с useCallback для стабильных ссылок
  // Убрал bringToFront() — он вызывает перерисовку всех полигонов
  const handleMouseOver = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseOut = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleClick = useCallback((e) => {
    if (onClick) {
      onClick(e);
    }
  }, [onClick]);

  // Собираем обработчики в один объект
  const eventHandlers = useMemo(() => ({
    mouseover: handleMouseOver,
    mouseout: handleMouseOut,
    click: handleClick,
  }), [handleMouseOver, handleMouseOut, handleClick]);

  return (
    <GradientPolygonComponent
      positions={positions}
      colorIndex={propColorIndex}
      isHovered={isHovered}
      eventHandlers={eventHandlers}
      renderer={sharedRenderer}
      pane={pane}
      tooltipContent={name}
    />
  );
};